import type {
  CustomerLifecycleMultiCards,
  CustomerLifecycleMultiCondition,
  CustomerLifecycleMultiDurationBuckets,
  CustomerLifecycleMultiDurationCondition,
  CustomerLifecycleMultiFunnel,
  CustomerLifecycleMultiItem,
  CustomerLifecycleSingleCondition,
  CustomerLifecycleSingleItem,
} from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { DateRanges } from '#/utils'

export const customerLifecycleApi = defineApi('operation', ({ prefix }) => {
  function buildSingleCondition(condition: CustomerLifecycleSingleCondition) {
    return {
      amOpenId: condition.amOpenId,
      bDOpenId: condition.bdOpenId,
      customerId: condition.customerId,
      orderBy: condition.orderBy,
      registeredAt: DateRanges.datesOrNull(condition.registeredAt) ?? undefined,
      sortBy: condition.sortBy,
    }
  }

  function buildMultiCondition(condition: CustomerLifecycleMultiCondition) {
    return {
      amOpenId: condition.amOpenId,
      bdOpenId: condition.bdOpenId,
      eventA: condition.eventA,
      eventB: condition.eventB,
      eventKey: condition.eventKey,
      maxDays: condition.maxDays,
      minDays: condition.minDays,
      orderBy: condition.orderBy,
      registeredAt: DateRanges.datesOrNull(condition.registeredAt) ?? undefined,
      sortBy: condition.sortBy,
    }
  }

  function getSingleCustomerList(
    params: PagingRequest<CustomerLifecycleSingleCondition>,
  ) {
    const requestParams = {
      condition: buildSingleCondition(params.condition),
      orderBy: params.orderBy,
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
    }

    return requestClient
      .post(prefix('customerLifecycle/single/customers'), requestParams)
      .then((res) => ({
        list:
          res.list?.map((item: CustomerLifecycleSingleItem) => ({
            am: item.am,
            bd: item.bd,
            cum100kAt: item.cum100kAt,
            cum10kAt: item.cum10kAt,
            cum1kAt: item.cum1kAt,
            cum5kAt: item.cum5kAt,
            customerId: item.customerId,
            customerName: item.customerName,
            firstRechargeAt: item.firstRechargeAt,
            firstSpendAt: item.firstSpendAt,
            onboardedAt: item.onboardedAt,
            paymentAt: item.paymentAt,
            registeredAt: item.registeredAt,
            stage: item.stage,
            stageLabel: item.stageLabel,
          })) ?? [],
        total: res.total,
      }))
  }

  function getSingleCustomerCards(condition: CustomerLifecycleSingleCondition) {
    return requestClient
      .post(
        prefix('customerLifecycle/single/cards'),
        buildSingleCondition(condition),
      )
      .then((res) => ({
        calcVersion: res.calcVersion,
        cost: res.cost,
        currentStage: res.currentStage,
        currentStageLabel: res.currentStageLabel,
        dataAsOf: res.dataAsOf,
        maxDaySpendUsd: res.maxDaySpendUsd,
        maxMonthSpendUsd: res.maxMonthSpendUsd,
        maxWeekSpendUsd: res.maxWeekSpendUsd,
        onboardToFirstRechargeDays: res.onboardToFirstRechargeDays,
        onboardToFirstSpendDays: res.onboardToFirstSpendDays,
        recent7dAvgSpendUsd: res.recent7dAvgSpendUsd,
        stageStayDays: res.stageStayDays,
        traceID: res.traceID,
      }))
  }

  function getSingleCustomerTimeline(
    condition: CustomerLifecycleSingleCondition,
  ) {
    return requestClient
      .post(
        prefix('customerLifecycle/single/timeline'),
        buildSingleCondition(condition),
      )
      .then((res) => ({
        calcVersion: res.calcVersion,
        cost: res.cost,
        dataAsOf: res.dataAsOf,
        gaps: res.gaps ?? [],
        nodes: res.nodes ?? [],
        traceID: res.traceID,
      }))
  }

  function getMultiCustomerList(
    params: PagingRequest<CustomerLifecycleMultiCondition>,
  ) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      orderBy: params.orderBy,
      condition: buildMultiCondition(params.condition),
    }

    return requestClient
      .post(prefix('customerLifecycle/multi/customers'), requestParams)
      .then((res) => ({
        list:
          res.list?.map((item: CustomerLifecycleMultiItem) => ({
            amOpenId: item.amOpenId,
            bdOpenId: item.bdOpenId,
            companyName: item.companyName,
            cumSpendTier: item.cumSpendTier,
            cumSpendUsd: item.cumSpendUsd,
            customerId: item.customerId,
            firstAccountSpendAt: item.firstAccountSpendAt,
            paymentAt: item.paymentAt,
            registeredAt: item.registeredAt,
          })) ?? [],
        total: res?.total,
      }))
  }

  function getMultiCustomerCards(condition: CustomerLifecycleMultiCondition) {
    return requestClient
      .post(
        prefix('customerLifecycle/multi/cards'),
        buildMultiCondition(condition),
      )
      .then(
        (res) =>
          ({
            calcVersion: res.calcVersion,
            cost: res.cost,
            cum10kConversionRate: res.cum10kConversionRate,
            dataAsOf: res.dataAsOf,
            firstAccountSpendConversionRate:
              res.firstAccountSpendConversionRate,
            paymentConversionRate: res.paymentConversionRate,
            totalCustomers: res.totalCustomers,
            traceID: res.traceID,
          }) satisfies CustomerLifecycleMultiCards,
      )
  }

  function getMultiCustomerFunnel(condition: CustomerLifecycleMultiCondition) {
    return requestClient
      .post(
        prefix('customerLifecycle/multi/funnel'),
        buildMultiCondition(condition),
      )
      .then(
        (res) =>
          ({
            baseCount: res.baseCount,
            calcVersion: res.calcVersion,
            cost: res.cost,
            dataAsOf: res.dataAsOf,
            steps: res.steps ?? [],
            traceID: res.traceID,
          }) satisfies CustomerLifecycleMultiFunnel,
      )
  }

  function getMultiCustomerDurationBuckets(
    condition: CustomerLifecycleMultiDurationCondition,
  ) {
    return requestClient
      .post(prefix('customerLifecycle/multi/durationBuckets'), {
        ...buildMultiCondition(condition),
        eventA: condition.eventA,
        eventB: condition.eventB,
      })
      .then(
        (res) =>
          ({
            buckets: res.buckets ?? [],
            calcVersion: res.calcVersion,
            cost: res.cost,
            dataAsOf: res.dataAsOf,
            eligibleCount: res.eligibleCount,
            traceID: res.traceID,
            unit: res.unit,
          }) satisfies CustomerLifecycleMultiDurationBuckets,
      )
  }

  return {
    getMultiCustomerCards,
    getMultiCustomerDurationBuckets,
    getMultiCustomerFunnel,
    getMultiCustomerList,
    getSingleCustomerCards,
    getSingleCustomerList,
    getSingleCustomerTimeline,
  }
})
